/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { message, Row, Col, Steps } from "antd";

import { ButtonPrimary, Button } from "../../../components/Common/Button/index";
import { CardElement } from "../../../components/Common/UIElements";
import CustomerDetailsForm from "../../../components/Onboarding/CustomerDetailsForm/CustomerDetailsForm";
import BillingDetailsForm from "../../../components/Onboarding/BillingDetailsForm/BillingDetailsForm";
import PropertyUploader from "../../../components/Onboarding/PropertyUploader/PropertyUploader";
import CorporateContactActivationMessage from "./CorporateContactActivation";

import {
  onboardingCustomerDetailsSchema,
  onboardingBillingDetailsSchema,
  onboardingPropertySchema,
} from "../../../schema/onboarding-customer.schema";

import {
  useCreateCustomer,
  useUpdateCustomer,
  useGetCustomer,
} from "../../../hooks/Onboarding/onboarding.hooks";
import { useActivateOnboardingUser } from "../../../hooks/Onboarding/onboarding-users.hooks";
import {
  useGetPropertiesByCustomer,
  useAddProperty,
} from "../../../hooks/Onboarding/onboarding-properties.hooks";
import useQuery from "../../../hooks/url.hooks";
import { ONBOARDING_CUSTOMER_STATUS } from "../../../utils/onboarding-helper";

// css
import "./styles.css";

const { Step } = Steps;

const OnboardingCustomerManager = (props) => {
  const { operation } = props;
  const query = useQuery();

  const [current, setCurrent] = useState(0);
  const [customerId, setCustomerId] = useState();
  const [isErrorState, setIsErrorState] = useState(false);

  useEffect(() => {
    const id = query.get("customer") ? query.get("customer") : undefined;
    setCustomerId(id);
  }, []);

  const customerSchema = useMemo(() => {
    switch (current) {
      case 0:
        return onboardingCustomerDetailsSchema;
      case 1:
        return onboardingBillingDetailsSchema;
      case 2:
        return onboardingPropertySchema;
      default:
        return onboardingCustomerDetailsSchema;
    }
  }, [current]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    resetField,
    clearErrors,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(customerSchema),
  });

  const {
    data: apiCustomerData,
    isLoading: isCustomerDataLoading,
    refetch: refetchCustomerData,
  } = useGetCustomer(customerId, {
    enabled: !!customerId,
  });
  const { data: apiPropertyData, isLoading: isPropertyDataLoading } =
    useGetPropertiesByCustomer(customerId, {
      enabled: !!customerId,
    });

  // set property data

  const propertyData = useMemo(() => {
    return apiPropertyData?.map((property) => {
      return {
        _id: property._id,
        property_name: property.property_name,
        property_code: property.property_code,
        brand: property.brand,
        pms: property.pms,
        pos: property.pos,
        property_time_zone: property.property_time_zone,
        property_contact: {
          _id: property?.property_contact_id?._id,
          first_name: property?.property_contact_id?.first_name,
          last_name: property?.property_contact_id?.last_name,
          email: property?.property_contact_id?.email,
        },
      };
    });
  }, [apiPropertyData]);

  useEffect(() => {
    return reset({
      ...apiCustomerData,

      propertyData,
    });
  }, [apiCustomerData, apiPropertyData]);

  const history = useHistory();
  const redirectHandler = () => {
    refetchCustomerData();
    history.push(`/onboarding/dashboard`);
  };

  const { mutateAsync: createCustomer, isLoading: isCustomerCreating } =
    useCreateCustomer({
      onSuccess: (result) => {
        setCustomerId(result?.newCustomer?._id);
        message.success("Corporate Customer saved in draft successfully");
      },
      onError: (error) => {
        setIsErrorState(true);
        message.error(error.response?.data?.message ?? "Server Error");
      },
    });
  const { mutateAsync: updateCustomer, isLoading: isCustomerUpdating } =
    useUpdateCustomer({
      onSuccess: () => {
        message.success("Corporate Customer Update in successfully");
      },
      onError: (error) => {
        setIsErrorState(true);
        message.error(error.response?.data?.message ?? "Server Error");
      },
    });

  const { mutateAsync: addProperty } = useAddProperty({
    onSuccess: () => {
      message.success("Property details Updated successfully");
    },
    onError: (error) => {
      setIsErrorState(true);
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const { mutate: activateCorporateContact, loading: activateContactLoading } =
    useActivateOnboardingUser({
      onSuccess: () => {
        message.success("Customer invitation sent successfully");
        redirectHandler();
      },
      onError: (error) => {
        message.error(error.response?.data?.message ?? "Server Error");
      },
    });

  const steps = [
    {
      title: "Add Customer",
      content: (
        <CustomerDetailsForm
          control={control}
          errors={errors}
          setValue={setValue}
          watch={watch}
          operation={operation}
          getValues={getValues}
        />
      ),
    },
    {
      title: "Add Billing Details",
      content: (
        <BillingDetailsForm
          control={control}
          errors={errors}
          setValue={setValue}
          watch={watch}
          resetField={resetField}
          getValues={getValues}
          clearErrors={clearErrors}
        />
      ),
    },
    {
      title: "Add Property",
      content: (
        <PropertyUploader
          control={control}
          errors={errors}
          customer={customerId}
          isAddPropertyDisabled={
            isCustomerCreating || isCustomerUpdating || isCustomerDataLoading
          }
        />
      ),
    },
    {
      title: "Create Corporate Contact",
      content: <CorporateContactActivationMessage />,
    },
  ];
  const next = () => {
    setCurrent(current + 1);
  };
  const back = () => {
    setCurrent(current - 1);
    setIsErrorState(false);
  };

  const addPropertyClick = async (data) => {
    // step 2 - properties
    if (customerId && data.propertyData.length > 0) {
      const { propertyData } = data;
      await addProperty({ customerId, propertyData });
    }
  };

  const SubmitFunction = async (data) => {
    data.status = ONBOARDING_CUSTOMER_STATUS.IN_DRAFT;
    if (!customerId) {
      await createCustomer(data);
    } else {
      await updateCustomer(data);
    }
    if (current >= 2) {
      await addPropertyClick(data);
    }
  };

  // onboarding customer and cooperate contact and property  create or update inside onboarding DB
  const saveDraftClick = async (data) => {
    await SubmitFunction(data);
    redirectHandler();
  };

  // onboarding customer and cooperate contact  create or update inside onboarding DB
  const onSubmit = async (data) => {
    if (current === 1) {
      SubmitFunction(data);
      next();
    }
    // step 3 with activation
    let result;
    if (current === 3) {
      data.status = ONBOARDING_CUSTOMER_STATUS.PENDING;
      if (!customerId) {
        result = await createCustomer(data);
      } else {
        result = await updateCustomer(data);
      }

      if (result !== undefined) {
        const updatedCustomerId =
          result?.newCustomer?._id ?? result?.updatedCustomer?._id;
        setCustomerId(updatedCustomerId);
        if (updatedCustomerId && data.propertyData.length > 0) {
          const { propertyData } = data;
          await addProperty({ customerId: updatedCustomerId, propertyData });
        }
        const updatedCorporateContactId =
          result?.updatedCorporateContact?._id ??
          result?.updatedCustomer?.corporate_contact_id?._id;

        activateCorporateContact({
          id: result?.newCorporateContact?._id ?? updatedCorporateContactId,
          status: ONBOARDING_CUSTOMER_STATUS.PENDING,
        });
      }
    }
  };

  // Customer activation - hmg, hotel.user create inside SM and send email
  const activateCorporateContactClick = (data) => {
    onSubmit(data);
  };

  // Next button Function handling order
  const SubmitDataFunctions = useMemo(() => {
    switch (current) {
      case 0:
        return next;
      case 1:
        return onSubmit;
      // case 2:
      //   return addPropertyClick;
      default:
        return next;
    }
  }, [current, customerId]);

  return (
    <CardElement title="System Discovery">
      <Row>
        <Col
          xxl={{ span: 18, offset: 3 }}
          xl={{ span: 18, offset: 3 }}
          lg={{ span: 18, offset: 3 }}
          md={{ span: 18, offset: 3 }}
          sm={{ span: 24, offset: 0 }}
          xs={{ span: 24, offset: 0 }}
        >
          <Steps current={current}>
            {steps.map((item) => (
              <Step key={item.title} title={item.title} />
            ))}
          </Steps>
        </Col>
      </Row>
      <Row>
        <div className="steps-content">{steps[current].content}</div>
      </Row>
      <Row>
        <Col span={16} offset={4}>
          <div className="steps-action">
            {current === 0 && (
              <Button onClick={() => history.push("/onboarding/dashboard")}>
                Cancel
              </Button>
            )}
            {current > 0 && <Button onClick={() => back()}>Back</Button>}
            <Button
              htmlType="submit"
              disabled={current === 0}
              onClick={handleSubmit(saveDraftClick)}
            >
              Save Draft
            </Button>
            {current < steps.length - 1 && (
              <ButtonPrimary
                onClick={handleSubmit(SubmitDataFunctions)}
                disabled={
                  isCustomerDataLoading ||
                  isPropertyDataLoading ||
                  isErrorState === true
                }
              >
                Next
              </ButtonPrimary>
            )}
            {current === steps.length - 1 && (
              <ButtonPrimary
                onClick={handleSubmit(activateCorporateContactClick)}
                style={{ width: "190px", paddingLeft: "10px" }}
                loading={activateContactLoading}
                disabled={isErrorState === true}
              >
                Activate Corporate Contact
              </ButtonPrimary>
            )}
          </div>
        </Col>
      </Row>
    </CardElement>
  );
};

export default OnboardingCustomerManager;
